import { WindingDir } from "geom/core";
import { polyLine2EdgeCount, polyLine2Reverse, polyLine2Shift, polyLine2WindingDir } from "geom/polyLine2";
import type { DocMigration } from "../docMigration";

function isVirtualRoofBlock(v: any): boolean {
  return typeof v === "object" && v.type === "VIRTUAL_ROOF_BLOCK";
}

function outlineEdgeCount(outline: Array<number>): number {
  return outline.length / 2 - 1;
}

function range(n: number): number[] {
  const a = new Array(n);
  for (let i = 0; i < n; i++) {
    a[i] = i;
  }
  return a;
}

function edgeIdsByOutline(outline: Array<number>): string[] {
  return range(outlineEdgeCount(outline)).map((_, i) => (i + 1).toString());
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_276: DocMigration = {
  maxSchemaVersion: "0.0.276",
  description: "Fix roof free-form perimeter winding and migrate custom hip edges.",
  apply(json: any): any {
    const { roofs, edgeModifiers } = json;

    const newRoofs = {};
    for (const roofId in roofs) {
      const roof = roofs[roofId];
      const { roofSettings, roofEdgeOverrides = {} } = roof;
      let { blocks, lowRailIndexForPeakDirection, roofVersion } = roof;

      // fix winding of free-form roofs
      if (isVirtualRoofBlock(blocks) && roofSettings?.boundaryType === "CLIP_TO_PERIMETER") {
        let { perimeter, edgeIds } = blocks;

        const edgeCount = polyLine2EdgeCount(perimeter);

        // fix old free form perimeters if they are CW
        if (polyLine2WindingDir(perimeter) === WindingDir.CW && edgeCount > 0) {
          // manually close it without adding a new edge
          perimeter[perimeter.length - 2] = perimeter[0];
          perimeter[perimeter.length - 1] = perimeter[1];

          // reverse the perimeter to make it CCW
          perimeter = polyLine2Reverse(perimeter);
          polyLine2Shift(perimeter, 1);

          // adjust edge index dependent values to match the new winding
          if (lowRailIndexForPeakDirection !== undefined) {
            lowRailIndexForPeakDirection = edgeCount - lowRailIndexForPeakDirection;
          }

          // adjust the edge modifiers to match the new winding
          for (const edgeModifierId in edgeModifiers) {
            const edgeModifier = edgeModifiers[edgeModifierId];
            if (edgeModifier.parentId !== roofId) {
              continue;
            }

            edgeModifiers[edgeModifierId] = {
              ...edgeModifier,
              edgeId: (edgeCount + 2 - parseInt(edgeModifier.edgeId)).toString(),
            };
          }

          // rebuild edge ids
          edgeIds = edgeIdsByOutline(perimeter);
        }

        // migrate roof version if there are any overrides
        for (const edgeId in roofEdgeOverrides) {
          const roofEdgeOverride = roofEdgeOverrides[edgeId];

          if (roofEdgeOverride?.overhang !== undefined || roofEdgeOverride?.slope !== undefined) {
            roofVersion = 5;
            break;
          }
        }

        // migrate custom hip edges to roofEdgeOverrides
        if (roof.hipEdges) {
          let hipEdgeShift = 0;
          if (roof.hipEdges.length >= 2) {
            roofVersion = 5;
            hipEdgeShift = 1;
          }

          for (const { edgeId, slope } of roof.hipEdges) {
            const newEdgeId = ((edgeId + hipEdgeShift) % edgeCount).toString();

            roofEdgeOverrides[newEdgeId] = {
              ...roofEdgeOverrides[newEdgeId],
              slope,
            };
          }
        }

        blocks = {
          ...blocks,
          perimeter,
          edgeIds,
        };
      }

      delete roof.hipEdges;

      newRoofs[roofId] = {
        ...roof,
        roofVersion,
        lowRailIndexForPeakDirection,
        blocks,
        roofEdgeOverrides,
      };
    }

    return {
      ...json,
      roofs: newRoofs,
    };
  },
};

export default migration_0_0_276;
